import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import Dropdown, {
  DropdownEventEnum,
} from "@booking/components/compounds/forms/Dropdown";
import CheckboxGroup from "@booking/components/containers/forms/CheckboxGroup";
import Fieldset from "@booking/components/containers/forms/Fieldset";
import { ButtonActionEnum, SizeEnum } from "@booking/enums";
import { useSubscribe } from "@booking/hooks/usePubSub";
import React, { useState } from "react";

import { CheckboxInputEventEnum } from "@/components/compounds/forms/CheckboxInput";
import { MAX_NIGHTS, MAX_ROUNDS } from "@/cyo/utils/constants";
import { createNumberOptions } from "@/cyo/utils/formHelpers";
import { ColorEnum } from "@/enums";
import type { PubSubData } from "@/types";

import { DESTINATIONS, DEPARTURE_AIRPORTS } from "../constants/enquiryData";
import styles from "./EnquiryForm.module.css";

interface EnquiryTravelFormProps {
  onContinue: (travelDetails: EnquiryTravelFormData) => void;
  initialData?: Partial<EnquiryTravelFormData>;
}

export interface EnquiryTravelFormData {
  destination: string;
  arrival_date: string;
  group_size: string;
  nights: string;
  rounds: string;
  include_flight_options: boolean;
  flight_options: string;
}

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

const initialFormData: EnquiryTravelFormData = {
  destination: "",
  arrival_date: getTomorrowDate(),
  group_size: "1",
  nights: "1",
  rounds: "1",
  include_flight_options: false,
  flight_options: "",
};

// Convert arrays to Dropdown options format
const groupSizeOptions = createNumberOptions(1, 10);
const nightsOptions = createNumberOptions(1, MAX_NIGHTS);
const roundsOptions = createNumberOptions(0, MAX_ROUNDS);

const flightOptions = DEPARTURE_AIRPORTS.map(({ text, value }) => ({
  text,
  value,
}));

const destinationOptions = DESTINATIONS.map(({ disabled, text, value }) => ({
  disabled,
  text,
  value,
}));

// Input field names
const INPUTS = {
  DESTINATION: "destination",
  ARRIVAL_DATE: "arrival_date",
  GROUP_SIZE: "group_size",
  NIGHTS: "nights",
  ROUNDS: "rounds",
  INCLUDE_FLIGHT_OPTIONS: "include_flight_options",
  FLIGHT_OPTIONS: "flight_options",
};

function EnquiryTravelForm(props: EnquiryTravelFormProps) {
  const { onContinue, initialData = {} } = props;
  const [formData, setFormData] = useState<EnquiryTravelFormData>({
    ...initialFormData,
    ...initialData,
  });

  function updateControl(
    data: PubSubData & {
      context: keyof EnquiryTravelFormData;
    },
  ) {
    updateForm({ [data.context]: data.value });
  }

  function updateForm(data: Partial<EnquiryTravelFormData>) {
    setFormData({ ...formData, ...data });

    if (data.include_flight_options === false) {
      setFormData((prev) => ({
        ...prev,
        flight_options: "",
      }));
    }
  }

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onContinue(formData);
  };

  // Subscribe to form events
  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [
    INPUTS.DESTINATION,
    INPUTS.GROUP_SIZE,
    INPUTS.NIGHTS,
    INPUTS.ROUNDS,
    INPUTS.FLIGHT_OPTIONS,
  ]);

  useSubscribe(CheckboxInputEventEnum.CHANGE, updateControl, [
    INPUTS.INCLUDE_FLIGHT_OPTIONS,
  ]);

  return (
    <form onSubmit={handleSubmit} className={styles.horizontalForm}>
      <div className={styles.formSection}>
        <Fieldset
          color={ColorEnum.SLATE}
          legend={"Travel Details"}
          legendBackground={false}
          noBorder
          noLegend
          noPadding
        >
          <div className={styles.formRow}>
            <div className={styles.formLabel}>
              <label htmlFor={INPUTS.DESTINATION}>Destination</label>
            </div>
            <div className={styles.formInput}>
              <Dropdown
                name={INPUTS.DESTINATION}
                options={destinationOptions}
                value={formData.destination}
                noLabel
                label="Destination"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formLabel}>
              <label htmlFor={INPUTS.ARRIVAL_DATE}>Arriving on</label>
            </div>
            <div className={styles.formInput}>
              <input
                type="date"
                id={INPUTS.ARRIVAL_DATE}
                name={INPUTS.ARRIVAL_DATE}
                value={formData.arrival_date}
                onChange={(e) =>
                  updateForm({ [INPUTS.ARRIVAL_DATE]: e.target.value })
                }
                required
                min={getTomorrowDate()}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formLabel}>
              <label htmlFor={INPUTS.GROUP_SIZE}>Group size</label>
            </div>
            <div className={styles.formInput}>
              <Dropdown
                name={INPUTS.GROUP_SIZE}
                options={groupSizeOptions}
                required
                value={formData.group_size}
                noLabel
                label="Group size"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formLabel}>
              <label htmlFor={INPUTS.NIGHTS}>Number of nights</label>
            </div>
            <div className={styles.formInput}>
              <Dropdown
                name={INPUTS.NIGHTS}
                options={nightsOptions}
                required
                value={formData.nights}
                noLabel
                label="Number of nights"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formLabel}>
              <label htmlFor={INPUTS.ROUNDS}>Number of rounds</label>
            </div>
            <div className={styles.formInput}>
              <Dropdown
                name={INPUTS.ROUNDS}
                options={roundsOptions}
                required
                value={formData.rounds}
                noLabel
                label="Number of rounds"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <CheckboxGroup
              className={styles.checkboxes}
              color={ColorEnum.SLATE}
              data={[
                {
                  children: [
                    <p key="flight_info_requested">
                      Yes, I'm interested in flight options
                    </p>,
                  ],
                  checked: formData.include_flight_options,
                  name: INPUTS.INCLUDE_FLIGHT_OPTIONS,
                  value: "has_flight",
                },
              ]}
              legend={"Flight Options"}
              name={"flight-options"}
              noBorder
              noLegend
            />
          </div>

          {formData.include_flight_options && (
            <div className={styles.formRow}>
              <div className={styles.formLabel}>
                <label htmlFor={INPUTS.FLIGHT_OPTIONS}>
                  Departure airport (optional)
                </label>
              </div>
              <div className={styles.formInput}>
                <Dropdown
                  name={INPUTS.FLIGHT_OPTIONS}
                  options={flightOptions}
                  required={false}
                  value={formData.flight_options}
                  noLabel
                  label="Departure airport (optional)"
                />
              </div>
            </div>
          )}
        </Fieldset>

        <div className={styles.formActions}>
          <Button
            color={ColorEnum.DARKGREEN}
            size={SizeEnum.LARGE}
            type={ButtonActionEnum.SUBMIT}
            variant={ButtonVariantEnum.SOLID}
          >
            GET THIS GOLF BREAK
          </Button>
        </div>

        <div className={styles.callInfo}>
          <p>
            Speak with someone who's been there! Call us on{" "}
            <a href="tel:08000436644">0800 043 6644</a> from 8:30AM today
          </p>
        </div>
      </div>
    </form>
  );
}

export default EnquiryTravelForm;
export { EnquiryTravelForm, type EnquiryTravelFormProps };
